import { Inject, Injectable } from "@angular/core";
import { APP_TYPE, AppType } from "../app.type";
import { CustomerName } from "../../../../shared/config/customers";
import { model2Doc, transferCase, transferPost } from "../api.service";
import { StringUtils } from "../../../../shared/utils/string.utils";
import { TransferServiceType } from "./transfer.service.type";

/**
 * Fields that are never carried into the receiving system.
 * The block at the end is the Internal Data section of the case
 * (list.thread.layer): it describes the sending system's own stock,
 * quantities and responsibles, none of which apply in the other system.
 */
const FIELDS_NOT_TRANSFERRED = [
  "_id",
  "_rev",
  "_attachments",
  "attachmentHistory",
  "omfNumber",
  "create_time",
  "update_time",
  "user_id",
  "update_user",
  "create_user_id",
  "update_user_id",
  "closedOn",
  "closedBy",
  "fav",
  "deputy",
  "deputyEmail",
  "transferredFrom",
  "transferredTo",
  "linkedOmfNumber",
  // Internal Data section
  "closingDate",
  "vehicleCnt",
  "actualStock",
  "dinCodeRespName",
  "engineerName",
  "buyer"
];
const BANNER_TIMEOUT = 3000;

@Injectable()
export class TransferService implements TransferServiceType {
  constructor(@Inject(APP_TYPE) private app: AppType) {}

  transferring = false;

  isCaseTransferred(): boolean {
    return !StringUtils.isNullOrEmpty(
      this.app.thread.thread[this.app.fieldId.thread.linkedOmfNumber]
    );
  }

  async transferCase(targetCustomer: CustomerName): Promise<void> {
    const sourceThreadId = this.app.thread.thread[this.app.fieldId.thread._id];
    if (StringUtils.isNullOrEmpty(sourceThreadId)) {
      return;
    }
    if (this.transferring) {
      return;
    }
    this.transferring = true;
    this.app.spinner.showSpinner();

    try {
      await transferCase(
        this.app.customers.expectCurrent,
        sourceThreadId,
        targetCustomer,
        this.buildTransferredThread()
      );
      await this.app.thread.getThread(sourceThreadId);
      this.app.successText = this.app.text.transfer.transferCaseSuccess;
      this.app.hasSuccess = true;
      setTimeout(() => {
        this.app.state.next({ hasSuccess: false });
      }, BANNER_TIMEOUT);
    } catch (error) {
      this.app.errorText = this.app.text.transfer.transferFailed;
      this.app.hasError = true;
      setTimeout(() => {
        this.app.state.next({ hasError: false });
      }, BANNER_TIMEOUT);
    } finally {
      this.transferring = false;
      this.app.spinner.hideSpinner();
    }
  }

  async transferPost(post: any, targetCustomer: CustomerName): Promise<void> {
    if (post == null || StringUtils.isNullOrEmpty(post._id)) {
      return;
    }
    if (this.transferring) {
      return;
    }
    this.transferring = true;
    this.app.spinner.showSpinner();

    try {
      await transferPost(
        this.app.customers.expectCurrent,
        post._id,
        targetCustomer,
        this.buildTransferredPost(post)
      );
      await this.app.post.getThreadPosts(
        this.app.customers.expectCurrent,
        this.app.thread.id ?? ""
      );
      this.app.successText = this.app.text.transfer.transferPostSuccess;
      this.app.hasSuccess = true;
      setTimeout(() => {
        this.app.state.next({ hasSuccess: false });
      }, BANNER_TIMEOUT);
    } catch (error) {
      this.app.errorText = this.app.text.transfer.transferFailed;
      this.app.hasError = true;
      setTimeout(() => {
        this.app.state.next({ hasError: false });
      }, BANNER_TIMEOUT);
    } finally {
      this.transferring = false;
      this.app.spinner.hideSpinner();
    }
  }

  /** The comment or solution as it should look in the receiving system. */
  buildTransferredPost(post: any): any {
    const copy: any = { ...post };
    FIELDS_NOT_TRANSFERRED.forEach((field) => {
      delete copy[field];
    });
    // the backend replaces this with the receiving case
    delete copy.snapshot;
    return copy;
  }

  isPostTransferred(post: any): boolean {
    if (post == null) {
      return false;
    }
    return !StringUtils.isNullOrEmpty(post.transferredTo);
  }

  buildTransferredThread(): any {
    const doc: any = model2Doc("thread", this.app.thread.thread);
    FIELDS_NOT_TRANSFERRED.forEach((field) => {
      delete doc[field];
    });
    return doc;
  }
}
